import type { GroupRef } from '@ralph/contracts'
import { useMemo } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import { useSession } from '@/hooks/use-api.ts'

/**
 * Who is looking, as the knowledge pages need it: an id to recognise "yours", whether they
 * administer (the "All" list, any owner, D-18), and the groups they are in and manage — which
 * the picker reads to predict what goes live now and what waits for review (D-13).
 */
export type KnowledgeViewer = {
  id: string | null
  displayName: string
  isAdmin: boolean
  groups: readonly GroupRef[]
  managedGroups: readonly GroupRef[]
}

const NONE: readonly GroupRef[] = []

export const useKnowledgeViewer = (): KnowledgeViewer => {
  const session = useSession()
  const user = session.data?.user ?? null
  return useMemo(
    () => ({
      id: user?.id ?? null,
      displayName: user?.displayName ?? '',
      isAdmin: user?.roles.includes('admin') ?? false,
      groups: user?.groups ?? NONE,
      managedGroups: user?.managedGroups ?? NONE
    }),
    [user]
  )
}

/**
 * The server's own sentence for a refusal — its copy is written for people — or a plain fallback.
 *
 * The fallback is a PARAMETER rather than a catalogue read, because this module is pure: a
 * module-scope capture of a catalogue branch freezes the language at import and then renders the
 * previous one with nothing failing (PRD-27 F-3, LOC-012). Callers pass `t.knowledge.genericError`
 * from inside their own body.
 *
 * The server's `message` is still English by D-4 — it is the operator's record — so this whole
 * path is Phase 3's to replace with `describeApiError` and a `messageKey` (LOC-033).
 */
export const messageOf = (error: unknown, fallback: string): string =>
  error instanceof ApiRequestError ? (error.issues[0]?.message ?? fallback) : fallback

/** Server sentences arrive lower-case and without a stop; a sentence on screen needs both. */
export const asSentence = (text: string): string => {
  const trimmed = text.trim()
  if (trimmed === '') return trimmed
  const capital = trimmed[0]?.toUpperCase() + trimmed.slice(1)
  return /[.!?]$/.test(capital) ? capital : `${capital}.`
}
